import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  Signal,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { loadHdChartGlobalSettings, saveHdChartGlobalSettings } from '../../chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdPlanetInfoService } from '../../chart/bodygraph/hd-planet-info.service';
import {
  HD_PLANET_ORDER_STANDARD,
  HdPlanetKey,
  getHdPlanetDisplayConfig,
} from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdCalendarDayBadge, HdMomentsCalendarComponent } from '../../chart/timeline/hd-moments-calendar.component';
import { HdTransitLegendFilterComponent } from '../legend-filter/hd-transit-legend-filter.component';
import { formatGateLine } from '../transit-countdown.utils';
import { TransitDataService } from '../transit-data.service';
import { legendCategoryOfKey } from '../transit-legend.utils';
import { HdTransitMomentAddRequest } from '../transit-moment-add.models';
import { TransitViewTimeService } from '../transit-view-time.service';
import { HdTransitsViewStateService } from '../transits-view-state.service';
import { HdTransitGateInfoMode, HdTransitPlanetSegments, TransitGranularity } from '../transit.models';
import { HdTransitDayFlowComponent } from './hd-transit-day-flow.component';
import {
  HdTransitLineDay,
  buildHdTransitCalendarDays,
  buildHdTransitLineDays,
  hdTransitAddDays,
  hdTransitDayKey,
  hdTransitDayStartMs,
} from './hd-transit-day-flow.builder';
import { HdTransitDayWindow } from './hd-transit-day-window';
import { HdTransitSunLineComponent } from './hd-transit-sun-line.component';
import { HdTransitWeekStripComponent } from './hd-transit-week-strip.component';

const I18N_PREFIX = 'humanDesign.chart.transits.vertical';
const MONTH_KEY_FORMAT = 'YYYY-MM';
const DAY_KEY_FORMAT = 'YYYY-MM-DD';
/** Vanaf deze breedte (px) staat de maandkalender rechts naast de dag-flow. */
const WIDE_MIN_WIDTH_PX = 720;
/**
 * Smal maar minstens zo breed (rechterpaneel): de poortinfo-schakelaar staat
 * in de weekstrip naast de kalenderknop. Smaller (telefoon) past hij daar niet
 * naast het maandlabel en blijft hij onder de legende.
 */
const GATE_INFO_IN_WEEK_STRIP_MIN_WIDTH_PX = 440;
/** Debounce op kalender-aanvragen (snel doorbladeren van maanden). */
const LOAD_DEBOUNCE_MS = 150;
/** Zoveel dagen rond het gerenderde venster moeten geladen zijn (anders bijladen)… */
const FLOW_REQUIRED_MARGIN_DAYS = 3;
/** …en zoveel dagen extra worden dan meegenomen (ruim onder de 92-dagen-cap per call). */
const FLOW_FETCH_MARGIN_DAYS = 10;
/** Lijn-dag van de weekstrip: een week marge aan elke kant, zodat ‹ › meteen klaar is. */
const WEEK_SUN_FETCH_MARGIN_DAYS = 7;
const MS_PER_MINUTE = 60_000;

/** Weergavevolgorde van de planeten: zoals de lanes en Nu & Straks, Chiron achteraan. */
const PLANET_ORDER: readonly HdPlanetKey[] = [...HD_PLANET_ORDER_STANDARD, 'chiron'];

interface LoadedRange {
  readonly fromMs: number;
  readonly toMs: number;
  readonly signature: string;
}

function emptyPlanetSegments(planetKey: HdPlanetKey): HdTransitPlanetSegments {
  return {
    planetId: getHdPlanetDisplayConfig(planetKey).planetId,
    sourceKey: planetKey,
    planetKey,
    segments: [],
    stations: [],
    retrogradeSpans: [],
  };
}

interface MsRange {
  readonly fromMs: number;
  readonly toMs: number;
}

/**
 * Lijn-segmenten van de zon voor één bereik (weekstrip of maandkalender),
 * laatste aanvraag wint. Los van de legende-filter: de lijn-dag staat er ook
 * als de zon niet aangevinkt is. Zolang de dag-flow of de kalender de zon al
 * op lijnniveau geladen heeft, komt alles uit de chunk-cache van de datalaag
 * (geen extra call).
 */
class HdSunLineSegmentsLoader {
  readonly sun = signal<HdTransitPlanetSegments | null>(null);
  private loaded: MsRange | null = null;
  private seq = 0;

  constructor(private readonly dataService: TransitDataService) {}

  /**
   * Laadt `target`, tenzij de geladen data `required` al dekt. BELANGRIJK: in
   * dat laatste geval vervalt een nog lopende aanvraag. Anders overschreef een
   * late lading van een verre week (sprong, meteen weer terug) de juiste data
   * en bleef de weekstrip leeg tot de volgende weekwissel (spec "een late
   * lading van een verlaten week overschrijft de lijn-dag niet").
   */
  ensure(required: MsRange, target: MsRange): void {
    if (this.loaded != null && this.loaded.fromMs <= required.fromMs && this.loaded.toMs >= required.toMs) {
      this.seq++;
      return;
    }
    void this.load(target);
  }

  private async load(target: MsRange): Promise<void> {
    const seq = ++this.seq;
    const [sun] = await this.dataService.getSegments(moment.utc(target.fromMs), moment.utc(target.toMs), ['sun'], 'line');
    if (seq !== this.seq) return;
    this.sun.set(sun ?? null);
    // Een mislukte call (geen segmenten) telt niet als geladen: later opnieuw.
    this.loaded = sun != null && sun.segments.length > 0 ? target : null;
  }
}

/**
 * Verticale transits-weergave (spec
 * `docs/superpowers/specs/2026-09-26-transits-verticale-tijdlijn-analyse.md`
 * §2.2): legende-filter, weekstrip, de dag-flow en de maandkalender met de
 * planeetglyphs van de wissels per dag. Breed (≥ 720 px eigen breedte: zwevend
 * paneel, transits-pagina) staat de kalender rechts; smal (rechter dock,
 * telefoon) klapt hij open via de kalenderknop in de weekstrip.
 *
 * - **Data dag-flow**: lijn-segmenten rond het gerenderde venster (±10 dagen,
 *   meteen bijgeladen zodra het venster minder dan 3 dagen marge heeft), enkel
 *   voor de planeten die de eigen legende-filter toont (standaard de zon).
 * - **Data kalender**: per getoonde maand(grid) poort- of lijn-segmenten naar
 *   de gekozen glyph-granulariteit (setting), enkel zolang de kalender zichtbaar is.
 * - **Focusdag**: de dag bovenaan de flow; weekstrip en kalender volgen hem,
 *   hun kliks laten de flow springen (`jumpTo`).
 * - **Lijn-dag van de zon** (Steven 2026-10-21): rechtsboven in elke tegel
 *   van de weekstrip en elke dagcel van de kalender de lijn van de zon die
 *   dag (bij een wissel alle lijnen, de dominante iets groter). Eigen
 *   zon-lijnsegmenten per week (± 1 week) en per kalendergrid, uit de
 *   chunk-cache zolang de dag-flow of de kalender de zon al laadt.
 * - Positie, kalendermaand en open-stand overleven een undock/dock-cyclus via
 *   de view-state-service.
 */
@Component({
  selector: 'hd-transits-vertical',
  imports: [
    NgTemplateOutlet,
    ESegmentedToggleComponent,
    HdTransitDayFlowComponent,
    HdTransitWeekStripComponent,
    HdMomentsCalendarComponent,
    HdTransitLegendFilterComponent,
    HdTransitSunLineComponent,
  ],
  templateUrl: './hd-transits-vertical.component.html',
  styleUrl: './hd-transits-vertical.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdTransitsVerticalComponent {
  readonly showChiron = input(loadHdChartGlobalSettings().showChiron);
  readonly compact = input(false);
  /**
   * Phone-sheet (Steven 2026-10-17): zo veel mogelijk dag-flow in beeld. De
   * planetenlegende zit dan achter een filterknop in de weekstrip en de
   * poortinfo-schakelaar is verborgen (de instelling blijft gelden).
   */
  readonly phone = input(false);
  /** "Nieuwe poort"-badge bij een poortwissel; het transits-paneel zet hem uit wanneer het gedockt is. */
  readonly gateShiftBadge = input(true);
  readonly momentAddRequested = output<HdTransitMomentAddRequest>();
  /** Just Now-knop op de nu-lijn (enkel de transit van nu). */
  readonly justNowRequested = output<void>();
  /** Rave New Year-badge op de zonwissel naar 41: het jaar. */
  readonly raveNewYearRequested = output<number>();

  private readonly dataService = inject(TransitDataService);
  private readonly viewTime = inject(TransitViewTimeService);
  private readonly viewState = inject(HdTransitsViewStateService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly flow = viewChild<HdTransitDayFlowComponent>('dayFlow');

  /** Eigen breedte van de weergave (ResizeObserver); 0 tot de eerste meting. */
  protected readonly hostWidth = signal(0);
  protected readonly wide = computed(() => this.hostWidth() >= WIDE_MIN_WIDTH_PX);
  /** Poortinfo-schakelaar in de weekstrip (smal, waar de kalenderknop staat) i.p.v. onder de legende. */
  protected readonly gateInfoInWeekStrip = computed(() =>
    !this.phone() && !this.wide() && this.hostWidth() >= GATE_INFO_IN_WEEK_STRIP_MIN_WIDTH_PX);
  /** Phone: legende (planetenfilter) uitgeklapt via de filterknop in de weekstrip. */
  protected readonly legendOpen = signal(false);
  /** De legende-rij: altijd buiten phone, op phone enkel na de filterknop. */
  protected readonly legendVisible = computed(() => !this.phone() || this.legendOpen());

  protected toggleLegend(): void {
    this.legendOpen.update(open => !open);
  }

  /** "Nu" op minuutresolutie: volgt de transits-kijktijd (live of gepind door de tijdslider). */
  protected readonly nowMinuteMs: Signal<number> = computed(
    () => Math.floor(this.viewTime.viewTime().valueOf() / MS_PER_MINUTE) * MS_PER_MINUTE,
  );
  protected readonly todayKey = computed(() => hdTransitDayKey(this.nowMinuteMs()));
  /**
   * Just Now enkel zolang de kijktijd live is: gepind door een tijdslider staat
   * de nu-lijn op dat moment, en Just Now is altijd het echte nu.
   */
  protected readonly justNowAvailable = this.viewTime.isLive;

  /**
   * Startpositie van de dag-flow: een verse bewaarde positie (undock/dock,
   * weergavewissel), anders vandaag. Eén keer gelezen.
   */
  private readonly savedFlowState = this.viewState.freshVerticalFlowState();
  protected readonly initialFocusKey = this.savedFlowState?.focusKey ?? hdTransitDayKey(this.viewTime.viewTime().valueOf());
  protected readonly initialOffsetPx = this.savedFlowState?.offsetPx ?? 0;

  /** De dag bovenaan de dag-flow. */
  protected readonly focusDayKey = signal(this.initialFocusKey);

  protected readonly calendarOpen = signal(this.viewState.isVerticalCalendarOpen());
  protected readonly calendarMonthKey = signal(
    this.viewState.verticalCalendarMonthKey() ?? this.initialFocusKey.slice(0, 7),
  );
  protected readonly badgeGranularity = signal<TransitGranularity>(
    loadHdChartGlobalSettings().transitsCalendarBadgeGranularity,
  );
  protected readonly granularities: readonly TransitGranularity[] = ['gate', 'line'];
  /** Poortinfo bij de wissels (setting): poortlink, badge met keynotes, of extra poortrij. */
  protected readonly gateInfoMode = signal<HdTransitGateInfoMode>(loadHdChartGlobalSettings().transitsGateInfoMode);
  protected readonly gateInfoModes: readonly HdTransitGateInfoMode[] = ['link', 'badge', 'row'];

  private readonly calendarVisible = computed(() => this.wide() || this.calendarOpen());

  /** Knoppen van de granulariteit-toggle (poort/lijn) in de kalendertoolbar. */
  protected readonly granularityOptions = computed<readonly ESegmentedToggleOption<TransitGranularity>[]>(() => {
    const text = this.text();
    return this.granularities.map(granularity => ({ value: granularity, label: text.granularity[granularity] }));
  });

  /** Knoppen van de poortinfo-toggle (link/badge/rij) naast de legende. */
  protected readonly gateInfoOptions = computed<readonly ESegmentedToggleOption<HdTransitGateInfoMode>[]>(() => {
    const text = this.text();
    return this.gateInfoModes.map(mode => ({
      value: mode,
      label: text.gateInfo[mode],
      title: text.gateInfoTooltip[mode],
    }));
  });

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly text = computed(() => {
    const lang = this.lang();
    const translate = (key: string) => this.transloco.translate<string>(`${I18N_PREFIX}.${key}`, undefined, lang);
    return {
      error: translate('errorState'),
      loading: translate('loading'),
      granularityLabel: translate('calendarGranularity.label'),
      granularity: {
        gate: translate('calendarGranularity.gate'),
        line: translate('calendarGranularity.line'),
      } as Record<TransitGranularity, string>,
      gateInfoLabel: translate('gateInfoMode.label'),
      legendFilterShow: translate('legendFilterShow'),
      legendFilterHide: translate('legendFilterHide'),
      gateInfo: {
        link: translate('gateInfoMode.link'),
        badge: translate('gateInfoMode.badge'),
        row: translate('gateInfoMode.row'),
      } as Record<HdTransitGateInfoMode, string>,
      gateInfoTooltip: {
        link: translate('gateInfoMode.linkTooltip'),
        badge: translate('gateInfoMode.badgeTooltip'),
        row: translate('gateInfoMode.rowTooltip'),
      } as Record<HdTransitGateInfoMode, string>,
    };
  });

  /** Alle planeten volgens de Chiron-setting. */
  private readonly planetKeys = computed<readonly HdPlanetKey[]>(() =>
    this.showChiron() ? PLANET_ORDER : PLANET_ORDER.filter(key => key !== 'chiron'),
  );

  /** De eigen legende-filter van de verticale weergave (standaard enkel de zon). */
  private readonly enabledCategories = this.viewState.enabledLegendCategoriesFor('vertical');

  /**
   * Zichtbare planeten — enkel díé worden geladen: de maan alleen levert ±12-16
   * lijnwissels per dag, dus alles laden "voor het geval dat" maakte de eerste
   * lading traag. Een planeet aanvinken laadt enkel haar ontbrekende dagen bij
   * (de chunk-cache van de datalaag bewaart de rest).
   */
  private readonly visiblePlanetKeys = computed<readonly HdPlanetKey[]>(() => {
    const enabled = this.enabledCategories();
    return this.planetKeys().filter(key => enabled.has(legendCategoryOfKey(key)));
  });

  // ── Dag-flow-data ────────────────────────────────────────────────────────

  private readonly renderedWindow = signal<HdTransitDayWindow | null>(null);
  private readonly flowSegments = signal<readonly HdTransitPlanetSegments[]>([]);
  /** Een lading voor de dag-flow is onderweg (laad-indicator). */
  protected readonly flowLoading = signal(false);
  private flowLoaded: LoadedRange | null = null;
  private flowInFlight: LoadedRange | null = null;
  private flowLoadSeq = 0;

  /**
   * Zichtbare planeten in weergavevolgorde, met hun segmenten. Een zichtbare
   * planeet zonder data (nog aan het laden) doet mee met lege segmenten: haar
   * dagen tonen dan "laden" in plaats van "geen wissels".
   */
  protected readonly flowPlanets = computed<readonly HdTransitPlanetSegments[]>(() => {
    const byKey = new Map(this.flowSegments().map(planet => [planet.planetKey, planet] as const));
    return this.visiblePlanetKeys().map(key => byKey.get(key) ?? emptyPlanetSegments(key));
  });

  // ── Kalender-data ────────────────────────────────────────────────────────

  private readonly calendarSegments = signal<readonly HdTransitPlanetSegments[]>([]);
  private calendarLoaded: LoadedRange | null = null;
  private calendarLoadSeq = 0;

  /** Het 6-wekengrid van de getoonde maand (zoals `hd-moments-calendar` het tekent). */
  private readonly calendarGrid = computed(() => {
    const gridStart = moment(this.calendarMonthKey(), MONTH_KEY_FORMAT).startOf('month').startOf('isoWeek');
    const fromKey = gridStart.format('YYYY-MM-DD');
    return { fromKey, toKey: hdTransitAddDays(fromKey, 41) };
  });

  protected readonly calendarBadges = computed<ReadonlyMap<string, readonly HdCalendarDayBadge[]>>(() => {
    const grid = this.calendarGrid();
    const lang = this.lang();
    const days = buildHdTransitCalendarDays(
      this.orderedVisible(this.calendarSegments()),
      grid.fromKey,
      grid.toKey,
      this.badgeGranularity(),
    );
    const badges = new Map<string, HdCalendarDayBadge[]>();
    for (const [dayKey, planets] of days) {
      badges.set(dayKey, planets.map(entry => {
        const config = getHdPlanetDisplayConfig(entry.planetKey);
        const name = this.planetInfo.planetInfoIn(entry.planetKey, lang)?.name || config.label;
        const shifts = entry.shifts
          .map(shift => `${moment(shift.atMs).format('HH:mm')} → ${formatGateLine(shift.gate, shift.line)}`)
          .join(', ');
        return {
          id: entry.planetKey,
          glyph: config.astronomiconChar,
          color: config.stripeColor,
          tooltip: `${name}: ${shifts}`,
          strong: entry.kind === 'gate',
        };
      }));
    }
    return badges;
  });

  // ── Lijn-dag van de zon (weekstrip + kalender) ─────────────────────────────

  /** Maandag van de week van de focusdag: de weekstrip toont ma-zo. */
  private readonly focusWeekStartKey = computed(() =>
    moment(this.focusDayKey(), DAY_KEY_FORMAT).startOf('isoWeek').format(DAY_KEY_FORMAT),
  );
  private readonly weekSun = new HdSunLineSegmentsLoader(this.dataService);
  private readonly calendarSun = new HdSunLineSegmentsLoader(this.dataService);

  protected readonly weekSunLines = computed<ReadonlyMap<string, HdTransitLineDay>>(() => {
    const startKey = this.focusWeekStartKey();
    return buildHdTransitLineDays(this.weekSun.sun(), startKey, hdTransitAddDays(startKey, 6));
  });

  protected readonly calendarSunLines = computed<ReadonlyMap<string, HdTransitLineDay>>(() => {
    const grid = this.calendarGrid();
    return buildHdTransitLineDays(this.calendarSun.sun(), grid.fromKey, grid.toKey);
  });

  protected readonly error = this.dataService.segmentsError;

  constructor() {
    // Dag-flow-data: meteen (bij)laden zodra het gerenderde venster buiten de
    // geladen — of al onderweg zijnde — marge valt. Geen debounce: elke stap
    // van de treadmill verschuift het venster, en een debounce die telkens
    // herstart stelde de eerste lading uit tot het venster stillag. De
    // dekkings-check op de lopende lading voorkomt een request-storm.
    effect(() => {
      const window = this.renderedWindow();
      const planets = this.visiblePlanetKeys();
      if (window == null) return;
      const signature = planets.join(',');
      const required = {
        fromMs: hdTransitDayStartMs(hdTransitAddDays(window.firstKey, -FLOW_REQUIRED_MARGIN_DAYS)),
        toMs: hdTransitDayStartMs(hdTransitAddDays(window.lastKey, FLOW_REQUIRED_MARGIN_DAYS + 1)),
      };
      if (this.covers(this.flowLoaded, required, signature) || this.covers(this.flowInFlight, required, signature)) return;
      const target: LoadedRange = {
        fromMs: hdTransitDayStartMs(hdTransitAddDays(window.firstKey, -FLOW_FETCH_MARGIN_DAYS)),
        toMs: hdTransitDayStartMs(hdTransitAddDays(window.lastKey, FLOW_FETCH_MARGIN_DAYS + 1)),
        signature,
      };
      untracked(() => void this.loadFlow(target, planets));
    });

    // Kalender-data: per getoonde maand en granulariteit, enkel zolang zichtbaar.
    effect(onCleanup => {
      if (!this.calendarVisible()) return;
      const grid = this.calendarGrid();
      const planets = this.visiblePlanetKeys();
      const granularity = this.badgeGranularity();
      const target: LoadedRange = {
        fromMs: hdTransitDayStartMs(grid.fromKey),
        toMs: hdTransitDayStartMs(hdTransitAddDays(grid.toKey, 1)),
        signature: `${granularity}|${planets.join(',')}`,
      };
      if (this.covers(this.calendarLoaded, target, target.signature)) return;
      const timer = setTimeout(() => void this.loadCalendar(target, planets, granularity), LOAD_DEBOUNCE_MS);
      onCleanup(() => clearTimeout(timer));
    });

    // Lijn-dag in de weekstrip: de week van de focusdag, met een week marge
    // aan elke kant. Scrollen binnen dat bereik laadt niets bij.
    effect(() => {
      const startKey = this.focusWeekStartKey();
      const required = {
        fromMs: hdTransitDayStartMs(startKey),
        toMs: hdTransitDayStartMs(hdTransitAddDays(startKey, 7)),
      };
      const target = {
        fromMs: hdTransitDayStartMs(hdTransitAddDays(startKey, -WEEK_SUN_FETCH_MARGIN_DAYS)),
        toMs: hdTransitDayStartMs(hdTransitAddDays(startKey, 7 + WEEK_SUN_FETCH_MARGIN_DAYS)),
      };
      untracked(() => this.weekSun.ensure(required, target));
    });

    // Lijn-dag in de kalender: per getoond maandgrid, enkel zolang zichtbaar
    // (debounce: snel doorbladeren van maanden).
    effect(onCleanup => {
      if (!this.calendarVisible()) return;
      const grid = this.calendarGrid();
      const range = {
        fromMs: hdTransitDayStartMs(grid.fromKey),
        toMs: hdTransitDayStartMs(hdTransitAddDays(grid.toKey, 1)),
      };
      const timer = setTimeout(() => this.calendarSun.ensure(range, range), LOAD_DEBOUNCE_MS);
      onCleanup(() => clearTimeout(timer));
    });

    // Eigen breedte volgen: breed/smal kiest de plek van de maandkalender.
    effect(onCleanup => {
      const element = this.hostElement.nativeElement;
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width != null && width > 0) this.ngZone.run(() => this.hostWidth.set(width));
      });
      this.ngZone.runOutsideAngular(() => observer.observe(element));
      onCleanup(() => observer.disconnect());
    });

    this.destroyRef.onDestroy(() => {
      this.viewState.saveVerticalCalendarMonth(this.calendarMonthKey());
      this.viewState.saveVerticalCalendarOpen(this.calendarOpen());
    });
  }

  /** Weekstrip of kalender: de flow springt naar de dag. */
  protected jumpTo(dayKey: string): void {
    this.focusDayKey.set(dayKey);
    this.flow()?.jumpTo(dayKey);
  }

  /** Nu-knop in de weekstrip: naar vandaag, met de nu-lijn in beeld. */
  protected jumpToNow(): void {
    this.focusDayKey.set(this.todayKey());
    this.flow()?.jumpToNow();
  }

  protected onFocusDayChanged(dayKey: string): void {
    this.focusDayKey.set(dayKey);
  }

  protected onWindowChanged(window: HdTransitDayWindow): void {
    this.renderedWindow.set(window);
  }

  /** In de smalle weergave klapt de kalender na een dagkeuze dicht (ruimte voor de flow). */
  protected onCalendarDaySelected(dayKey: string): void {
    this.jumpTo(dayKey);
    if (!this.wide()) this.setCalendarOpen(false);
  }

  protected onCalendarMonthChanged(monthKey: string): void {
    this.calendarMonthKey.set(monthKey);
    this.viewState.saveVerticalCalendarMonth(monthKey);
  }

  protected toggleCalendar(): void {
    this.setCalendarOpen(!this.calendarOpen());
  }

  protected setBadgeGranularity(granularity: TransitGranularity): void {
    if (granularity === this.badgeGranularity()) return;
    this.badgeGranularity.set(granularity);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), transitsCalendarBadgeGranularity: granularity });
  }

  protected setGateInfoMode(mode: HdTransitGateInfoMode): void {
    if (mode === this.gateInfoMode()) return;
    this.gateInfoMode.set(mode);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), transitsGateInfoMode: mode });
  }

  private setCalendarOpen(open: boolean): void {
    this.calendarOpen.set(open);
    this.viewState.saveVerticalCalendarOpen(open);
  }

  private orderedVisible(segments: readonly HdTransitPlanetSegments[]): HdTransitPlanetSegments[] {
    const byKey = new Map(segments.map(planet => [planet.planetKey, planet] as const));
    return this.visiblePlanetKeys()
      .map(key => byKey.get(key))
      .filter((planet): planet is HdTransitPlanetSegments => planet != null);
  }

  private covers(loaded: LoadedRange | null, required: { fromMs: number; toMs: number }, signature: string): boolean {
    return loaded != null
      && loaded.signature === signature
      && loaded.fromMs <= required.fromMs
      && loaded.toMs >= required.toMs;
  }

  private async loadFlow(target: LoadedRange, planets: readonly HdPlanetKey[]): Promise<void> {
    const seq = ++this.flowLoadSeq;
    this.flowInFlight = target;
    this.flowLoading.set(true);
    try {
      const result = planets.length === 0
        ? []
        : await this.dataService.getSegments(moment.utc(target.fromMs), moment.utc(target.toMs), planets, 'line');
      if (seq !== this.flowLoadSeq) return; // verouderd antwoord (sneller gescrold of gefilterd)
      this.flowSegments.set(result);
      // Een mislukte call (lege lijst voor een niet-lege planetenset) telt niet
      // als geladen: bij het volgende venster wordt opnieuw geprobeerd.
      this.flowLoaded = result.length > 0 || planets.length === 0 ? target : null;
    } finally {
      if (seq === this.flowLoadSeq) {
        this.flowInFlight = null;
        this.flowLoading.set(false);
      }
    }
  }

  private async loadCalendar(
    target: LoadedRange,
    planets: readonly HdPlanetKey[],
    granularity: TransitGranularity,
  ): Promise<void> {
    const seq = ++this.calendarLoadSeq;
    const result = await this.dataService.getSegments(moment.utc(target.fromMs), moment.utc(target.toMs), planets, granularity);
    if (seq !== this.calendarLoadSeq) return;
    this.calendarSegments.set(result);
    this.calendarLoaded = result.length > 0 ? target : null;
  }
}
